import React from 'react';
import { CurrentHouseProvider } from '@/context/CurrentHouseContext';
import ServicesContent from '@/components/services/page/ServicesContent';
import WithPropertyRole from '@/components/WithPropertyRole';
import { Permission } from '@/utils/permissionUtils';
import { servicesTabFromSearch } from '@/components/services/page/servicesPageTab';

/**
 * Service providers page scoped to a specific property via its public ID in the URL.
 */
const ServiceProviders: React.FC = () => {
	const { contactId, reviewCandidateId, review, tab } = Route.useSearch();

	return (
		<CurrentHouseProvider>
			{/* The same guard as Access, for a reader typing the URL the sidebar no longer offers. */}
			<WithPropertyRole requiredPermissions={[Permission.HOUSE_NETWORK_VIEW]}>
				<ServicesContent
					initialContactId={contactId}
					initialReviewCandidateId={reviewCandidateId}
					openReviewQueue={review === 'open'}
					initialTab={tab}
				/>
			</WithPropertyRole>
		</CurrentHouseProvider>
	);
};

export const Route = createFileRoute({
	validateSearch: (search: Record<string, unknown>) => {
		const asId = (value: unknown) => {
			const id = typeof value === 'number' ? value : Number.parseInt(String(value), 10);
			return Number.isFinite(id) ? id : undefined;
		};

		return {
			contactId: asId(search.contactId),
			/**
			 * The candidate the review pass is reading, so a notification can address one directly and
			 * Back returns to it. Names an `extracted_contacts` row, which `contactId` never can.
			 */
			reviewCandidateId: asId(search.reviewCandidateId),
			/**
			 * A request to open the pass at the head of the queue, which is what the
			 * `contacts.candidates.added` notification targets — it counts candidates and names none.
			 * Spelled as the schedule spells its own review request, and consumed on arrival.
			 */
			review: search.review === 'open' ? ('open' as const) : undefined,
			/** The tab to land on, so a reveal after a write can say where the record went and a refresh reproduces it. */
			tab: servicesTabFromSearch(search.tab),
		};
	},
	component: ServiceProviders,
});
